var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { getHttpStatus } from "./importErrors";
/** Tools #76358: only the publisher itself creates its new prefix, when it assigns a number. */
export var NEW_PREFIX_BY_PUBLISHER_ONLY_MESSAGE_ID = "ui-br-agency.title.products.panel.assign.reason.newPrefixByPublisherOnly";
/** RCK-1667: the remaining free numbers are reserved by another assignment in progress. */
export var NUMBERS_RESERVED_MESSAGE_ID = "ui-br-agency.title.products.panel.assign.reason.numbersReserved";
/** RCK-1667: the publisher is in liquidation and has no free number. */
export var PUBLISHER_CLOSING_MESSAGE_ID = "ui-br-agency.title.products.panel.assign.reason.publisherClosing";
/** RCK-1667: no free number and the agency has no free prefix range for a new prefix. */
export var NO_PREFIX_RANGE_MESSAGE_ID = "ui-br-agency.title.products.panel.assign.reason.noPrefixRange";
/** RCK-1667: no free number could be drawn from the publisher prefix (inconsistent prefix data). */
export var PREFIX_NO_FREE_NUMBER_MESSAGE_ID = "ui-br-agency.title.products.panel.assign.reason.prefixNoFreeNumber";
/** No agency prefix is configured for the registration agency. */
export var NO_AGENCY_PREFIX_MESSAGE_ID = "ui-br-agency.title.products.panel.assign.reason.noAgencyPrefix";
/** RCK-1667: the free numbers are being assigned by a concurrent assignment right now; retry later. */
export var NUMBER_ASSIGNMENT_IN_PROGRESS_MESSAGE_ID = "ui-br-agency.title.products.panel.assign.reason.numberAssignmentInProgress";
/** RCK-1667: the publisher prefix the user chose has no free number left. */
export var CHOSEN_PREFIX_NO_FREE_NUMBER_MESSAGE_ID = "ui-br-agency.title.products.panel.assign.reason.chosenPrefixNoFreeNumber";
/** RCK-1685: no number could be drawn from the publisher's active prefixes and no new prefix was created. */
export var NO_AVAILABLE_NUMBER_MESSAGE_ID = "ui-br-agency.title.products.panel.assign.reason.noAvailableNumber";
/** RCK-1667: the agency has several agency prefixes; only the assign page lets the user choose one. */
export var AGENCY_PREFIX_CHOICE_REQUIRED_MESSAGE_ID = "ui-br-agency.title.products.panel.assign.reason.agencyPrefixChoiceRequired";
/**
 * RCK-1667: the message of each reason why no number can be drawn now, shared by the assign preview
 * ({@code assignBlockedReason}), the feasibility check and the create-with-number response ({@code reason}).
 */
export var NO_NUMBER_REASON_MESSAGE_IDS = {
    NUMBERS_RESERVED: NUMBERS_RESERVED_MESSAGE_ID,
    PUBLISHER_CLOSING: PUBLISHER_CLOSING_MESSAGE_ID,
    NO_PREFIX_RANGE: NO_PREFIX_RANGE_MESSAGE_ID,
    PREFIX_NO_FREE_NUMBER: PREFIX_NO_FREE_NUMBER_MESSAGE_ID,
    NO_AGENCY_PREFIX: NO_AGENCY_PREFIX_MESSAGE_ID,
    NUMBER_ASSIGNMENT_IN_PROGRESS: NUMBER_ASSIGNMENT_IN_PROGRESS_MESSAGE_ID,
    CHOSEN_PREFIX_NO_FREE_NUMBER: CHOSEN_PREFIX_NO_FREE_NUMBER_MESSAGE_ID,
    AGENCY_PREFIX_CHOICE_REQUIRED: AGENCY_PREFIX_CHOICE_REQUIRED_MESSAGE_ID,
};
/**
 * Maps a backend {@code assignBlockedReason} code (see the mod-br-agency
 * {@code ProductNumberAssignResponse.BlockedReason} constants) to the localized
 * message id that explains, in Czech, why a product number cannot be assigned.
 *
 * The read-only assign preview (GET /product-numbers/assign/{productId}/by-product-id)
 * returns HTTP 200 with one of these reasons instead of failing, so the assign page and
 * modal can render an explanation and disable saving rather than showing a blank page.
 */
export var ASSIGN_BLOCKED_REASON_MESSAGE_IDS = __assign({ PRODUCT_ALREADY_HAS_ASSIGNED_NUMBER: "ui-br-agency.product-number.assign.blocked.alreadyAssigned", PRODUCT_PUBLISHING_STATUS_NOT_ASSIGNABLE: "ui-br-agency.product-number.assign.blocked.publishingStatus", PUBLISHER_CLOSED: "ui-br-agency.product-number.assign.error.publisherClosed", PUBLISHER_GUARANTOR_DATA_NOT_CONFIRMED: "ui-br-agency.product-number.assign.error.confirmData", 
    // RECEK-75389: every active prefix of the publisher awaits the last-used-number
    // declaration; the hint says where the declaration is entered.
    PREFIX_DECLARATION_PENDING: "ui-br-agency.title.products.panel.assign.reason.declarationPending.hint", NEW_PREFIX_BY_PUBLISHER_ONLY: NEW_PREFIX_BY_PUBLISHER_ONLY_MESSAGE_ID }, NO_NUMBER_REASON_MESSAGE_IDS);
/** Generic fallback shown for an unrecognized (or newly added) blocking reason. */
export var ASSIGN_BLOCKED_GENERIC_MESSAGE_ID = "ui-br-agency.product-number.assign.blocked.generic";
/**
 * Resolve the localized message id for a blocking reason, or {@code null} when the
 * product number can be assigned (no reason present).
 *
 * @param reason the backend {@code assignBlockedReason} code, if any
 * @returns the message id to display, or {@code null} when not blocked
 */
export var getAssignBlockedReasonMessageId = function (reason) {
    var _a;
    if (!reason) {
        return null;
    }
    return ((_a = ASSIGN_BLOCKED_REASON_MESSAGE_IDS[reason]) !== null && _a !== void 0 ? _a : ASSIGN_BLOCKED_GENERIC_MESSAGE_ID);
};
/** RCK-1667: the co-edition assignment cannot choose the agency prefix, so several of them stop it. */
export var CO_EDITION_AGENCY_PREFIX_CHOICE_REQUIRED_MESSAGE_ID = "ui-br-agency.co-edition.assign.reason.agencyPrefixChoiceRequired";
/**
 * The notice of a dialog that offers no choice of publisher or prefix: the blocking reason, the reason why the
 * selection yields no number, or - when the backend could not determine the agency prefix because the agency has
 * several - where it can be chosen (the assign page; the co-edition assignment cannot choose it at all).
 *
 * @param assignment the assign preview
 * @param options {@code coEdition} for the co-edition dialog
 * @returns the message id to display, or {@code null} when nothing stops the assignment
 */
export var getAssignNoticeMessageId = function (assignment, options) {
    var _a;
    if (options === void 0) { options = {}; }
    var reasonMessageId = (_a = getAssignBlockedReasonMessageId(assignment === null || assignment === void 0 ? void 0 : assignment.assignBlockedReason)) !== null && _a !== void 0 ? _a : getAssignBlockedReasonMessageId(assignment === null || assignment === void 0 ? void 0 : assignment.noNumberReason);
    if (reasonMessageId) {
        return reasonMessageId;
    }
    if (!(assignment === null || assignment === void 0 ? void 0 : assignment.agencyPrefixChoiceRequired)) {
        return null;
    }
    return options.coEdition
        ? CO_EDITION_AGENCY_PREFIX_CHOICE_REQUIRED_MESSAGE_ID
        : AGENCY_PREFIX_CHOICE_REQUIRED_MESSAGE_ID;
};
/**
 * react-query {@code retry} predicate for the assign preview query: never retry a
 * client (4xx) error — those are deterministic and re-requesting only delays the error
 * pane — but keep retrying transient (network / 5xx) failures a couple of times.
 *
 * @param failureCount how many times the query has already failed
 * @param error the thrown error
 * @returns whether react-query should retry
 */
export var assignQueryRetry = function (failureCount, error) {
    var status = getHttpStatus(error);
    if (status !== undefined && status >= 400 && status < 500) {
        return false;
    }
    return failureCount < 2;
};
